import React, { useMemo, useState } from "react";
import { MdChevronRight, MdExpandMore, MdInbox } from "react-icons/md";
import { ProjectTask, TaskType } from "../../../../interfaces/ProjectTask";

interface Props {
  tasks: ProjectTask[];
  workedByTask: Record<number, number>;
  /** projectId → nome do cliente. */
  clientNameByProject: Record<number, string>;
  /** id do colaborador → nome (dimensão Colaborador). */
  namesById: Map<number, string>;
  /** Colaboradores filtrados (vazio = todos). */
  selectedEmpIds: number[];
}

type Dimension = "project" | "employee" | "client";
const DIM_LABEL: Record<Dimension, string> = {
  project: "Projeto › Épico › Tarefa",
  employee: "Colaborador › Tarefa",
  client: "Cliente › Projeto",
};

interface Node {
  key: string;
  label: string;
  orcado: number;
  realizado: number;
  children: Node[];
  isLeaf: boolean;
}

interface LeafRow {
  task: ProjectTask;
  orcado: number;
  realizado: number;
}

const fmtH = (n: number) => `${(Math.round(n * 10) / 10).toLocaleString("pt-BR", { maximumFractionDigits: 1 })}h`;

const CapacityBudgetVsActual: React.FC<Props> = ({
  tasks,
  workedByTask,
  clientNameByProject,
  namesById,
  selectedEmpIds,
}) => {
  const [dim, setDim] = useState<Dimension>("project");
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const toggle = (k: string) =>
    setCollapsed((p) => {
      const n = new Set(p);
      if (n.has(k)) n.delete(k);
      else n.add(k);
      return n;
    });

  const byId = useMemo(() => new Map(tasks.map((t) => [t.id, t])), [tasks]);
  const parentIds = useMemo(() => {
    const s = new Set<number>();
    tasks.forEach((t) => t.parentTaskId != null && s.add(t.parentTaskId));
    return s;
  }, [tasks]);

  const epicOf = (t: ProjectTask): ProjectTask | null => {
    let cur: ProjectTask | undefined = t;
    let g = 0;
    while (cur && g++ < 12) {
      if (cur.type === TaskType.Epic) return cur;
      if (cur.parentTaskId == null) return null;
      cur = byId.get(cur.parentTaskId);
    }
    return null;
  };

  // Folhas (não-pais), filtradas por colaborador, com orçado/realizado.
  const leaves = useMemo<LeafRow[]>(() => {
    const matches = (t: ProjectTask) => {
      if (selectedEmpIds.length === 0) return true;
      const ids = new Set<number>();
      if (t.assigneeEmployeeId != null) ids.add(t.assigneeEmployeeId);
      t.assignees?.forEach((a) => ids.add(a.id));
      return selectedEmpIds.some((id) => ids.has(id));
    };
    return tasks
      .filter((t) => !parentIds.has(t.id) && t.status !== undefined && matches(t))
      .map((t) => ({ task: t, orcado: t.estimatedHours ?? 0, realizado: workedByTask[t.id] ?? 0 }))
      .filter((l) => l.orcado > 0 || l.realizado > 0);
  }, [tasks, parentIds, workedByTask, selectedEmpIds]);

  // Extratores de nível (key estável + rótulo) por dimensão.
  const levels: ((l: LeafRow) => { key: string; label: string })[] = useMemo(() => {
    if (dim === "employee") {
      return [
        (l) => {
          const id = l.task.assigneeEmployeeId;
          return { key: `e${id ?? "0"}`, label: id != null ? namesById.get(id) ?? l.task.assigneeName ?? `#${id}` : "Sem responsável" };
        },
        (l) => ({ key: `t${l.task.id}`, label: l.task.title }),
      ];
    }
    if (dim === "client") {
      return [
        (l) => {
          const c = clientNameByProject[l.task.projectId];
          return { key: `c:${c ?? "—"}`, label: c ?? "Sem cliente" };
        },
        (l) => ({ key: `p${l.task.projectId}`, label: l.task.projectName ?? `Projeto #${l.task.projectId}` }),
      ];
    }
    // project › epic › task
    return [
      (l) => ({ key: `p${l.task.projectId}`, label: l.task.projectName ?? `Projeto #${l.task.projectId}` }),
      (l) => {
        const ep = epicOf(l.task);
        return { key: ep ? `ep${ep.id}` : "ep0", label: ep ? ep.title : "Sem épico" };
      },
      (l) => ({ key: `t${l.task.id}`, label: l.task.title }),
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dim, namesById, clientNameByProject, byId]);

  // Constrói a árvore agregada.
  const tree = useMemo<Node[]>(() => {
    const build = (rows: LeafRow[], lvl: number, prefix: string): Node[] => {
      const groups = new Map<string, { label: string; rows: LeafRow[] }>();
      rows.forEach((r) => {
        const { key, label } = levels[lvl](r);
        const g = groups.get(key) ?? { label, rows: [] };
        g.rows.push(r);
        groups.set(key, g);
      });
      return Array.from(groups.entries())
        .map(([key, g]) => {
          const orcado = g.rows.reduce((a, r) => a + r.orcado, 0);
          const realizado = g.rows.reduce((a, r) => a + r.realizado, 0);
          const last = lvl === levels.length - 1;
          return {
            key: `${prefix}/${key}`,
            label: g.label,
            orcado,
            realizado,
            isLeaf: last,
            children: last ? [] : build(g.rows, lvl + 1, `${prefix}/${key}`),
          };
        })
        .sort((a, b) => b.orcado - a.orcado || a.label.localeCompare(b.label));
    };
    return build(leaves, 0, "");
  }, [leaves, levels]);

  const totals = useMemo(
    () => leaves.reduce((a, l) => ({ orcado: a.orcado + l.orcado, realizado: a.realizado + l.realizado }), { orcado: 0, realizado: 0 }),
    [leaves]
  );

  const renderRows = (nodes: Node[], depth: number): React.ReactNode =>
    nodes.map((n) => {
      const pct = n.orcado > 0 ? (n.realizado / n.orcado) * 100 : n.realizado > 0 ? 999 : 0;
      const over = n.orcado > 0 && n.realizado > n.orcado + 0.001;
      const open = !collapsed.has(n.key);
      const hasKids = n.children.length > 0;
      return (
        <React.Fragment key={n.key}>
          <tr className="border-b border-gray-100 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-navy-700">
            <td className="px-4 py-3 pr-2">
              <div className="flex items-center gap-1" style={{ paddingLeft: depth * 16 }}>
                <button
                  type="button"
                  aria-label={open ? "Recolher" : "Expandir"}
                  onClick={() => hasKids && toggle(n.key)}
                  className={`flex h-4 w-4 shrink-0 items-center justify-center text-gray-400 ${hasKids ? "hover:text-gray-700 dark:hover:text-gray-200" : "invisible"}`}
                >
                  {open ? <MdExpandMore size={16} aria-hidden="true" /> : <MdChevronRight size={16} aria-hidden="true" />}
                </button>
                <span className={`truncate text-sm ${depth === 0 ? "font-bold text-navy-700 dark:text-white" : "text-gray-700 dark:text-gray-300"}`} title={n.label}>
                  {n.label}
                </span>
              </div>
            </td>
            <td className="px-4 py-3 text-right text-sm tabular-nums text-gray-700 dark:text-gray-300">{fmtH(n.orcado)}</td>
            <td className={`px-4 py-3 text-right text-sm font-semibold tabular-nums ${over ? "text-red-600 dark:text-red-400" : "text-green-600 dark:text-green-400"}`}>
              {fmtH(n.realizado)}
            </td>
            <td className="px-4 py-3 text-right text-sm tabular-nums text-gray-700 dark:text-gray-300">{fmtH(n.orcado - n.realizado)}</td>
            <td className="w-40 px-4 py-3">
              <div className="flex items-center gap-2">
                <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-gray-100 dark:bg-navy-900">
                  <div className={`h-full ${over ? "bg-red-500" : pct >= 80 ? "bg-amber-500" : "bg-green-500"}`} style={{ width: `${Math.min(100, pct)}%` }} />
                </div>
                <span className="w-12 text-right text-xs tabular-nums text-gray-700 dark:text-gray-300">{n.orcado > 0 ? `${Math.round(pct)}%` : "—"}</span>
              </div>
            </td>
          </tr>
          {open && hasKids && renderRows(n.children, depth + 1)}
        </React.Fragment>
      );
    });

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h4 className="text-sm font-bold text-navy-700 dark:text-white">Orçado × Realizado</h4>
          <p className="text-xs text-gray-700 dark:text-gray-300">
            Horas estimadas vs apontadas (acumulado). Total: <strong>{fmtH(totals.orcado)}</strong> orçado ·{" "}
            <strong>{fmtH(totals.realizado)}</strong> realizado.
          </p>
        </div>
        <div className="flex rounded-xl border border-gray-200 p-0.5 dark:border-white/10">
          {(Object.keys(DIM_LABEL) as Dimension[]).map((d) => (
            <button
              key={d}
              type="button"
              onClick={() => setDim(d)}
              className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition ${
                dim === d ? "bg-dv-gradient text-white shadow-dv-sm" : "text-gray-700 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-navy-700"
              }`}
            >
              {DIM_LABEL[d]}
            </button>
          ))}
        </div>
      </div>

      {tree.length === 0 ? (
        <div className="flex flex-col items-center gap-2 rounded-2xl border border-dashed border-gray-200 py-10 text-center text-sm text-gray-700 dark:border-white/10 dark:text-gray-300">
          <MdInbox className="h-7 w-7 text-gray-400" aria-hidden="true" />
          Sem tarefas com estimativa ou horas apontadas para os filtros selecionados.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-gray-100 bg-white nina-surface shadow-dv-md dark:border-white/10 dark:bg-navy-800">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-navy-900">
              <tr>
                <th className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wide text-gray-700 dark:text-gray-300">{DIM_LABEL[dim]}</th>
                <th className="px-4 py-3 text-right text-[11px] font-bold uppercase tracking-wide text-gray-700 dark:text-gray-300">Orçado</th>
                <th className="px-4 py-3 text-right text-[11px] font-bold uppercase tracking-wide text-gray-700 dark:text-gray-300">Realizado</th>
                <th className="px-4 py-3 text-right text-[11px] font-bold uppercase tracking-wide text-gray-700 dark:text-gray-300">Saldo</th>
                <th className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wide text-gray-700 dark:text-gray-300">Consumo</th>
              </tr>
            </thead>
            <tbody>{renderRows(tree, 0)}</tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default CapacityBudgetVsActual;
